import { CreateQuestionInput, QuestionTypeKey } from 'src/graphql';
import { QuestionData, ANSWER_TYPES, OPTION_BASED_TYPES } from '../types';
import {
  toOptionTranslationInputs,
  toQuestionTranslationInputs,
} from './toGraphqlQuestionTranslationInputs';

const ANSWER_TYPE_TO_QUESTION_TYPE_KEY: Record<string, QuestionTypeKey> = {
  [ANSWER_TYPES.SHORT_TEXT]: QuestionTypeKey.ShortText,
  [ANSWER_TYPES.LONG_TEXT]: QuestionTypeKey.LongText,
  [ANSWER_TYPES.RADIO]: QuestionTypeKey.Radio,
  [ANSWER_TYPES.CHECKBOX]: QuestionTypeKey.Checkbox,
  [ANSWER_TYPES.DROPDOWN_SINGLE]: QuestionTypeKey.Dropdown,
  [ANSWER_TYPES.DROPDOWN_MULTI]: QuestionTypeKey.MultipleChoice,
  [ANSWER_TYPES.DATA_GRID]: QuestionTypeKey.DataGrid,
  [ANSWER_TYPES.ATTACHMENT]: QuestionTypeKey.FileUpload,
  [ANSWER_TYPES.DATE_PICKER]: QuestionTypeKey.DateTime,
  [ANSWER_TYPES.TIME_PICKER]: QuestionTypeKey.Time,
  [ANSWER_TYPES.NUMERIC]: QuestionTypeKey.Number,
  [ANSWER_TYPES.DATABASE_FIELD]: QuestionTypeKey.DatabaseField,
};

function getQuestionTypeKey(questionType: string | undefined): QuestionTypeKey {
  if (!questionType) {
    return QuestionTypeKey.ShortText;
  }
  return (
    ANSWER_TYPE_TO_QUESTION_TYPE_KEY[questionType] ?? QuestionTypeKey.ShortText
  );
}

export function mapQuestionsToCreateInput(
  questions: QuestionData[],
  options?: { omitQuestionIds?: boolean; isDisclosureForm?: boolean },
): CreateQuestionInput[] {
  const omitQuestionIds = options?.omitQuestionIds ?? false;
  const isDisclosureForm = options?.isDisclosureForm ?? false;

  return questions.map((question) => {
    const trimmedText = question.questionText.trim();
    const trimmedDescription = question.description.trim();

    const baseInput: CreateQuestionInput = {
      questionText: trimmedText,
      questionType: getQuestionTypeKey(question.questionType),
      isRequired: question.isRequired,
      attachmentAllowed: false,
      description: trimmedDescription || null,
      interestTypeId: null,
    };

    // Only include shouldAutoFlagIfAnswered when it's true
    if (isDisclosureForm && question.flagIfAnswered === true) {
      baseInput.shouldAutoFlagIfAnswered = true;
    }

    const qTranslations = toQuestionTranslationInputs(question.translations);
    if (qTranslations?.length) {
      baseInput.translations = qTranslations;
    }

    if (!omitQuestionIds && question.id) {
      baseInput.id = question.id;
    }

    // Only include options for question types that support them
    if (question.questionType === ANSWER_TYPES.DATA_GRID) {
      if (question.options?.length) {
        baseInput.dataGridConfig = {
          ...(question.dataGridConfig ?? {}),
          columns: question.options.map((option) => {
            const trimmedLabel = option.text.trim();

            // Map column translations: optionText → label
            const columnTranslations = option.translations
              ?.filter((t) => t.languageCode.trim() && t.optionText.trim())
              .map((t) => ({
                languageCode: t.languageCode.trim(),
                label: t.optionText.trim(),
              }));

            return {
              key: trimmedLabel.toLowerCase().replace(/\s+/g, '_'),
              label: trimmedLabel,
              type: option.nextAction || 'text',
              required: false,
              ...(columnTranslations?.length && {
                translations: columnTranslations,
              }),
            };
          }),
        };
      } else if (question.dataGridConfig) {
        baseInput.dataGridConfig = question.dataGridConfig;
      }
    } else if (
      question.questionType &&
      OPTION_BASED_TYPES.has(question.questionType) &&
      question.options?.length
    ) {
      baseInput.options = question.options.map((option, index) => {
        const optTranslations = toOptionTranslationInputs(option.translations);

        // Transform frontend nextAction to backend navigationAction
        let navigationAction: string = 'next_question';
        let targetQuestionId: string | undefined;

        if (option.nextAction) {
          if (option.nextAction === 'next') {
            navigationAction = 'next_question';
          } else if (option.nextAction === 'submit') {
            navigationAction = 'submit_form';
          } else if (option.nextAction.startsWith('question:')) {
            const extractedId = option.nextAction.slice('question:'.length);
            if (extractedId) {
              navigationAction = 'jump_to_question';
              targetQuestionId = extractedId;
            } else {
              console.warn(
                '[mapQuestionsToCreateInput] question: prefix with empty ID',
                { optionIndex: index },
              );
            }
          }
        }

        const optionInput = {
          optionText: option.text.trim(),
          optionOrder: index,
          navigationAction,
          ...(targetQuestionId !== undefined && { targetQuestionId }),
          ...(isDisclosureForm &&
            option.isFlagged === true && { shouldAutoFlag: true }),
          ...(optTranslations?.length && { translations: optTranslations }),
        };

        return optionInput;
      });
    }

    if (question.questionType === ANSWER_TYPES.DATABASE_FIELD) {
      if (question.databaseFieldOptions?.selectedFieldId) {
        baseInput.databaseFieldSubTypeId =
          question.databaseFieldOptions.selectedFieldId;
      }
      baseInput.allowMultiSelect =
        question.databaseFieldOptions?.allowMultiSelect ?? false;
    }

    return baseInput;
  });
}
